// =============================================================================
// app/src/lib/codigoDaPeca.ts — qual código mostrar para a equipe, e de onde.
//
// Pedido do dono (10/09/2026): "gostaria que o código da peça aparecesse para
// nós no sistema, em orçamento, por exemplo".
//
// O código existe em lugares diferentes conforme a peça:
//
//   PEÇA DIVERSA .... `codigo_avulso`, digitado no balcão (071). É o único que
//                     ela tem — não há cadastro por trás.
//   PEÇA CADASTRADA . o do FABRICANTE, que é o que se usa para pedir ao
//                     fornecedor e para achar o similar. O `sku` é o código da
//                     LOJA: serve para bipar a etiqueta, não para comprar.
//
// A ordem abaixo é essa distinção, e não uma escala de qualidade: primeiro o
// que identifica a peça no mundo (fabricante, OEM), depois o que a identifica
// aqui dentro (sku). Documento de cliente não usa nada disto — ver a régua e
// os testes em reciboDoc/orcamentoDoc.
// =============================================================================

export interface PecaComCodigo {
  /** Congelado no documento; presente na peça diversa (071). */
  codigo_avulso?: string | null;
  /** Congelado na emissão — o código da LOJA. */
  sku?: string | null;
  /** Do cadastro, quando a peça ainda existe no catálogo. */
  produto?: {
    codigo_fabrica?: string | null;
    codigo_oem?: string | null;
    sku?: string | null;
  } | null;
}

const limpo = (v: string | null | undefined): string => (v ?? '').trim();

/**
 * O código a mostrar na tela, ou string vazia quando não há nenhum.
 *
 * Vazio é resposta legítima e comum: peça diversa que ninguém digitou o código,
 * ou cadastro sem código de fábrica. A tela mostra "—" e segue — inventar um
 * palpite aqui seria pior que o branco.
 */
export function codigoDaPeca(i: PecaComCodigo): string {
  const avulso = limpo(i.codigo_avulso);
  if (avulso) return avulso;

  const fabrica = limpo(i.produto?.codigo_fabrica);
  if (fabrica) return fabrica;

  const oem = limpo(i.produto?.codigo_oem);
  if (oem) return oem;

  // O sku da LINHA (congelado) antes do sku do cadastro: o documento vale pelo
  // que ele congelou. '9999' nunca: é o coringa, e dizer "9999" no lugar do
  // código não informa nada a ninguém.
  const skuDaLinha = limpo(i.sku);
  if (skuDaLinha && skuDaLinha !== '9999') return skuDaLinha;

  const skuDoCadastro = limpo(i.produto?.sku);
  if (skuDoCadastro && skuDoCadastro !== '9999') return skuDoCadastro;

  return '';
}

/** De onde veio o código — a tela usa para explicar em uma palavra. */
export function origemDoCodigo(i: PecaComCodigo): 'digitado' | 'fabricante' | 'oem' | 'loja' | null {
  if (limpo(i.codigo_avulso)) return 'digitado';
  if (limpo(i.produto?.codigo_fabrica)) return 'fabricante';
  if (limpo(i.produto?.codigo_oem)) return 'oem';
  const sku = limpo(i.sku) || limpo(i.produto?.sku);
  if (sku && sku !== '9999') return 'loja';
  return null;
}
